import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  DataTable,
  Text,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];

export default function SentOrders() {
  const navigate = useNavigate();
  // Legacy "Sent Orders" defaults to today's dispatches
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'sent-orders', startDate, endDate],
    queryFn: () => reportsApi.getSentOrders({ startDate, endDate }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/sent-orders', { startDate, endDate });
    } finally {
      setExporting(false);
    }
  };

  const rows = (data?.data || []).map((item) => [
    <Button key={item.rentalId} variant="plain" onClick={() => navigate(`/rentals/${item.rentalId}`)}>
      {item.orderNumber || item.rentalId.slice(0, 8)}
    </Button>,
    item.shipDate ? new Date(item.shipDate).toLocaleString() : '—',
    item.carrier || '—',
    item.trackingNumber || '—',
    item.clinicName || 'Direct',
    item.customerName || '—',
    item.products.join(', ') || '—',
  ]);

  return (
    <Page
      title="Sent Orders"
      subtitle="Rentals dispatched in the selected period (defaults to today)"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the Sent Orders report. Try refreshing.</p>
            </EmptyState>
          ) : !data || data.data.length === 0 ? (
            <EmptyState heading="No dispatches" image="">
              <p>No orders were dispatched in the selected period.</p>
            </EmptyState>
          ) : (
            <BlockStack gap="400">
              <Text as="p" variant="bodyMd" tone="subdued">
                {data.summary.totalSent} orders dispatched in this period
              </Text>
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Order', 'Ship Date', 'Carrier', 'Tracking', 'Clinic', 'Customer', 'Products']}
                rows={rows}
              />
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
